import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Camera, Plus, ChevronRight, CheckCircle2, Clock, AlertTriangle,
  Eye, Send, ArrowLeft, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Select, Input, Spinner, EmptyState,
} from "@/components/ui";
import { fmtDateTime } from "@/lib/dates";

// =============================================================================
// Acceptance Packs list + creation flow
// =============================================================================
// Every job has at least one pack. Some have multiple (staged handover).
// Field crew work on 'draft' and 'in_progress' packs. Managers do QA.
// Directors publish.
// =============================================================================

export default function AcceptancePacksScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"active" | "qa" | "published" | "all">("active");
  const [creating, setCreating] = useState(false);

  const { data: packs = [], isLoading } = useQuery({
    queryKey: ["acceptance_packs", filter],
    queryFn: async () => {
      let q = supabase
        .from("acceptance_packs")
        .select(`
          pack_id, pack_number, pack_title, status, items_total, items_complete,
          started_at, submitted_for_qa_at, qa_approved_at, published_at,
          projects(project_code, project_name),
          template:checklist_templates(display_name, version)
        `)
        .order("started_at", { ascending: false })
        .limit(100);

      if (filter === "active")    q = q.in("status", ["draft", "in_progress"]);
      if (filter === "qa")        q = q.in("status", ["qa_review", "ready_to_publish"]);
      if (filter === "published") q = q.in("status", ["published", "accepted"]);
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Acceptance Packs</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Evidenced photo packs delivered to clients at handover. Watermarked on-device, offline-capable.
          </div>
        </div>
        {hasRole(user, "supervisor") && (
          <Button icon={Plus} onClick={() => setCreating(true)}>New pack</Button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {([
          ["active",    "Active"],
          ["qa",        "QA queue"],
          ["published", "Published"],
          ["all",       "All"],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-wider2 font-semibold border transition-colors ${
              filter === k ? "bg-accent/12 text-accent border-accent/30" : "bg-ink-raised text-text-muted border-line hover:text-text"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner size={18}/></div>
      ) : packs.length === 0 ? (
        <Card><EmptyState title="No packs in this view" description="Create one to get started." icon={Camera} /></Card>
      ) : (
        <div className="space-y-2">
          {packs.map(p => {
            const pct = p.items_total > 0 ? Math.round((p.items_complete / p.items_total) * 100) : 0;
            const tone: any = p.status === "published" || p.status === "accepted"
              ? "approved"
              : p.status === "qa_review"
                ? "warning"
                : p.status === "ready_to_publish"
                  ? "approved"
                  : p.status === "rejected_by_client"
                    ? "critical"
                    : "mobilising";
            return (
              <Link key={p.pack_id} to={`/packs/${p.pack_id}`}>
                <Card>
                  <div className="p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-accent/12 text-accent flex items-center justify-center shrink-0">
                      <Camera size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[11px] text-accent">{p.pack_number}</span>
                        <Pill label={p.status.replace(/_/g, " ")} tone={tone} small />
                        {p.projects?.project_code && (
                          <span className="font-mono text-[11px] text-text-muted">· {p.projects.project_code}</span>
                        )}
                      </div>
                      <div className="text-[14px] text-text mt-0.5 truncate">
                        {p.pack_title}
                      </div>
                      <div className="text-[11px] text-text-muted font-mono mt-1">
                        {p.template?.display_name ?? "—"} · {p.items_complete}/{p.items_total} items
                      </div>
                      <div className="mt-2 h-1 bg-ink-raised rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            p.status === "rejected_by_client" ? "bg-danger" :
                            p.status === "published" || p.status === "accepted" ? "bg-ok" :
                            "bg-accent"
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-text-dim mt-2 shrink-0" />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {creating && <NewPackDialog onClose={() => setCreating(false)} onCreated={(id) => navigate(`/packs/${id}`)} />}
    </div>
  );
}

// =============================================================================
// "New pack" dialog — pick project + template + title
// =============================================================================
function NewPackDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (packId: string) => void }) {
  const user = useRequireAuth();
  const [project_id, setProjectId] = useState<string | null>(null);
  const [template_id, setTemplateId] = useState<string | null>(null);
  const [pack_title, setTitle] = useState("");

  const { data: projects = [] } = useQuery({
    queryKey: ["projects_for_pack"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, carrier_id, carriers(carrier_code)")
        .in("status", ["live", "mobilising", "acceptance_pending"])
        .order("project_code");
      if (error) throw error;
      return data as any[];
    },
  });

  const selectedProject = projects.find((p: any) => p.project_id === project_id);

  // Restrict templates to those matching the project's carrier if there is one
  const { data: templates = [] } = useQuery({
    queryKey: ["checklist_templates", selectedProject?.carrier_id],
    queryFn: async () => {
      let q = supabase
        .from("checklist_templates")
        .select("template_id, template_code, display_name, version, carrier_id")
        .eq("is_active", true)
        .order("display_name");
      if (selectedProject?.carrier_id) {
        q = q.or(`carrier_id.eq.${selectedProject.carrier_id},carrier_id.is.null`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
    enabled: !!project_id,
  });

  const createMut = useMutation({
    mutationFn: async () => {
      if (!project_id || !template_id) throw new Error("missing_fields");

      // Create pack
      const { data: pack, error: e1 } = await supabase
        .from("acceptance_packs")
        .insert({
          project_id,
          template_id,
          pack_title,
          assembled_by_employee_id: user?.employee_id,
          status: "draft",
          started_at: new Date().toISOString(),
        })
        .select("pack_id")
        .single();
      if (e1) throw e1;

      // Populate from template items — one acceptance_checklist_items row per
      // template item. Using .select + .insert chain to keep it simple.
      const { data: items, error: e2 } = await supabase
        .from("checklist_template_items")
        .select("template_item_id, sort_order")
        .eq("template_id", template_id);
      if (e2) throw e2;

      if (items && items.length > 0) {
        const rows = items.map(i => ({
          pack_id: pack.pack_id,
          template_item_id: i.template_item_id,
          status: "pending",
          sort_order: i.sort_order ?? 0,
        }));
        const { error: e3 } = await supabase.from("acceptance_checklist_items").insert(rows);
        if (e3) throw e3;
      }

      return pack.pack_id;
    },
    onSuccess: (id) => onCreated(id),
  });

  const canSubmit = !!project_id && !!template_id && pack_title.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center" onClick={onClose}>
      <div
        className="bg-ink-card border border-line w-full md:max-w-md md:rounded-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 border-b border-line flex items-start justify-between">
          <h3 className="text-lg font-semibold">New acceptance pack</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Project">
            <Select value={project_id ?? ""} onChange={e => { setProjectId(e.target.value || null); setTemplateId(null); }}>
              <option value="">— select —</option>
              {projects.map((p: any) => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_code} · {p.project_name}{p.carriers?.carrier_code ? ` (${p.carriers.carrier_code})` : ""}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Checklist template" hint={selectedProject ? "Filtered to templates valid for this project's carrier." : "Pick a project first."}>
            <Select value={template_id ?? ""} onChange={e => setTemplateId(e.target.value || null)} disabled={!project_id}>
              <option value="">— select —</option>
              {templates.map((t: any) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.display_name} (v{t.version})
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Pack title" hint="Short description, shown to the client.">
            <Input
              value={pack_title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. NBN install acceptance"
            />
          </Field>
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            icon={CheckCircle2}
            onClick={() => createMut.mutate()}
            disabled={!canSubmit || createMut.isPending}
          >
            {createMut.isPending ? "Creating…" : "Create pack"}
          </Button>
        </div>
      </div>
    </div>
  );
}
